import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, Plus, CheckSquare, CalendarDays, Grid3x3, Columns3,
  AlignLeft, Users, LayoutList, RotateCcw
} from 'lucide-react';
import { CalendarEvent } from '../../hooks/useSupabaseEvents';
import { Todo } from '../../hooks/useSupabaseKanban';
import { Calendar as CalendarType } from '../../types';
import { Space } from '../../contexts/SpaceContext';
import { getTonalColors } from '../../utils/calendar/constants';
import { useTheme } from '../../contexts/ThemeContext';

type ViewMode = 'year' | 'month' | 'week' | 'schedule' | 'todo';

// ── Result item types ─────────────────────────────────────────────────────────

type CommandItem = {
  kind: 'command';
  id: string;
  label: string;
  hint?: string;
  icon: React.ElementType;
  action: () => void;
};

type EventItem   = { kind: 'event';    event: CalendarEvent; cal?: CalendarType };
type TodoItem    = { kind: 'todo';     todo: Todo };
type CalItem     = { kind: 'calendar'; cal: CalendarType };
type SpaceItem   = { kind: 'space';    space: Space };

type AnyItem = CommandItem | EventItem | TodoItem | CalItem | SpaceItem;

interface Section { title: string; items: AnyItem[] }

// ── Date helpers ──────────────────────────────────────────────────────────────

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL  = ['january','february','march','april','may','june','july','august','september','october','november','december'];
const MONTHS_ABR   = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const DAYS         = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

function formatEventDate(dates: string[]): string {
  if (!dates.length) return '';
  const first = new Date(dates[0] + 'T00:00:00');
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((first.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const label = `${MONTHS_SHORT[first.getMonth()]} ${first.getDate()}`;
  if (dates.length === 1) return label;
  const last = new Date(dates[dates.length - 1] + 'T00:00:00');
  if (last.getMonth() === first.getMonth()) return `${label}–${last.getDate()}`;
  return `${label} – ${MONTHS_SHORT[last.getMonth()]} ${last.getDate()}`;
}

function formatTodoDue(due: string | null): { label: string; overdue: boolean } | null {
  if (!due) return null;
  const d = new Date(due + 'T00:00:00');
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return { label: 'Today', overdue: false };
  if (diff === 1) return { label: 'Tomorrow', overdue: false };
  if (diff === -1) return { label: 'Yesterday', overdue: true };
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, overdue: true };
  if (diff <= 7) return { label: `In ${diff}d`, overdue: false };
  return { label: `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`, overdue: false };
}

function parseQueryAsDate(q: string): Date | null {
  const s = q.toLowerCase().trim();
  const today = new Date(); today.setHours(0,0,0,0);

  if (s === 'today') return new Date(today);
  if (s === 'tomorrow') { const d = new Date(today); d.setDate(d.getDate()+1); return d; }
  if (s === 'yesterday') { const d = new Date(today); d.setDate(d.getDate()-1); return d; }

  const di = DAYS.indexOf(s);
  if (di !== -1) {
    const d = new Date(today);
    const diff = (di - d.getDay() + 7) % 7 || 7;
    d.setDate(d.getDate() + diff);
    return d;
  }

  const nextMatch = s.match(/^next (.+)$/);
  if (nextMatch) {
    const ni = DAYS.indexOf(nextMatch[1]);
    if (ni !== -1) {
      const d = new Date(today);
      const diff = (ni - d.getDay() + 7) % 7 || 7;
      d.setDate(d.getDate() + diff);
      return d;
    }
  }

  // "june 15" / "15 june" / "jun 15" / "15 jun"
  const mdMatch = s.match(/^([a-z]+)\s+(\d{1,2})$/) || s.match(/^(\d{1,2})\s+([a-z]+)$/);
  if (mdMatch) {
    let monthStr: string, day: number;
    if (isNaN(Number(mdMatch[1]))) { monthStr = mdMatch[1]; day = Number(mdMatch[2]); }
    else { monthStr = mdMatch[2]; day = Number(mdMatch[1]); }
    let mi = MONTHS_FULL.indexOf(monthStr);
    if (mi === -1) mi = MONTHS_ABR.indexOf(monthStr);
    if (mi !== -1 && day >= 1 && day <= 31) {
      const d = new Date(today.getFullYear(), mi, day);
      if (d < today) d.setFullYear(d.getFullYear() + 1);
      return d;
    }
  }

  return null;
}

function matchesQuery(text: string, q: string): boolean {
  return text.toLowerCase().includes(q.toLowerCase());
}

// ── Tonal color dot ───────────────────────────────────────────────────────────

function ColorDot({ color, isDark }: { color: string; isDark: boolean }) {
  const t = getTonalColors(color);
  const bg = isDark ? t.bgDark : t.bgLight;
  const text = isDark ? t.textDark : t.textLight;
  return (
    <span
      className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center text-[11px] font-light"
      style={{ backgroundColor: bg, color: text }}
    >
      A
    </span>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  events: CalendarEvent[];
  todos: Todo[];
  calendars: CalendarType[];
  spaces: Space[];
  currentSpace: Space | null;
  currentView: ViewMode;
  onNavigateToDate: (date: Date, view?: ViewMode) => void;
  onOpenEvent: (event: CalendarEvent) => void;
  onOpenTodo: (todo: Todo) => void;
  onSwitchSpace: (space: Space | null) => void;
  onNewEvent: (title?: string) => void;
  onNewTask: (title?: string) => void;
  onSwitchView: (view: ViewMode) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function GlobalSearch({
  isOpen, onClose,
  events, todos, calendars, spaces, currentSpace,
  currentView,
  onNavigateToDate, onOpenEvent, onOpenTodo, onSwitchSpace,
  onNewEvent, onNewTask, onSwitchView,
}: GlobalSearchProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [isOpen]);

  const close = useCallback(() => { setQuery(''); onClose(); }, [onClose]);

  // ── Build sections ──────────────────────────────────────────────────────────

  const sections: Section[] = useMemo(() => {
    const q = query.trim();
    const ql = q.toLowerCase();
    const result: Section[] = [];

    // ── Date navigation command ──
    const parsedDate = q ? parseQueryAsDate(q) : null;
    if (parsedDate) {
      const label = `${MONTHS_SHORT[parsedDate.getMonth()]} ${parsedDate.getDate()}, ${parsedDate.getFullYear()}`;
      result.push({
        title: '',
        items: [{
          kind: 'command',
          id: 'go-to-date',
          label: `Go to ${label}`,
          icon: CalendarDays,
          action: () => { onNavigateToDate(parsedDate, 'month'); close(); },
        }],
      });
    }

    // ── Quick actions ──
    const quickCommands: CommandItem[] = [
      { kind:'command', id:'new-event',    label:'New event',          icon: Plus,         action: () => { onNewEvent();          close(); } },
      { kind:'command', id:'new-task',     label:'New task',           icon: CheckSquare,  action: () => { onNewTask();           close(); } },
      { kind:'command', id:'today',        label:'Go to today',        icon: RotateCcw,    action: () => { onNavigateToDate(new Date()); close(); } },
      { kind:'command', id:'view-week',    label:'Week view',          icon: Columns3,     action: () => { onSwitchView('week');   close(); } },
      { kind:'command', id:'view-month',   label:'Month view',         icon: Grid3x3,      action: () => { onSwitchView('month');  close(); } },
      { kind:'command', id:'view-year',    label:'Year view',          icon: CalendarDays, action: () => { onSwitchView('year');   close(); } },
      { kind:'command', id:'view-schedule',label:'Schedule view',      icon: AlignLeft,    action: () => { onSwitchView('schedule'); close(); } },
      { kind:'command', id:'view-tasks',   label:'Tasks view',         icon: LayoutList,   action: () => { onSwitchView('todo');   close(); } },
    ];

    const filteredCommands = q
      ? quickCommands.filter(c => matchesQuery(c.label, ql))
      : quickCommands.slice(0, 5); // only top 5 when empty

    if (filteredCommands.length > 0) {
      result.push({ title: q ? 'Actions' : 'Quick actions', items: filteredCommands });
    }

    // ── Events ──
    if (q) {
      const matched = events
        .filter(e => !e.isImported && (matchesQuery(e.title, ql) || matchesQuery(e.notes || '', ql)))
        .slice(0, 6)
        .map(e => ({
          kind: 'event' as const,
          event: e,
          cal: e.calendar_id ? calendars.find(c => c.id === e.calendar_id) : undefined,
        }));
      if (matched.length) result.push({ title: 'Events', items: matched });
    }

    // ── Todos ──
    if (q) {
      const matched = todos
        .filter(t => !t.archived_at && matchesQuery(t.title, ql))
        .slice(0, 5)
        .map(t => ({ kind: 'todo' as const, todo: t }));
      if (matched.length) result.push({ title: 'Tasks', items: matched });
    }

    // ── Calendars ──
    if (q) {
      const matched = calendars
        .filter(c => matchesQuery(c.name, ql))
        .map(c => ({ kind: 'calendar' as const, cal: c }));
      if (matched.length) result.push({ title: 'Calendars', items: matched });
    }

    // ── Spaces ──
    if (q) {
      const matched = spaces
        .filter(s => matchesQuery(s.name, ql))
        .map(s => ({ kind: 'space' as const, space: s }));
      if (matched.length) result.push({ title: 'Spaces', items: matched });
    }

    // ── Create with prefill ──
    if (q) {
      result.push({
        title: '',
        items: [
          {
            kind: 'command',
            id: 'create-event',
            label: `Create event "${q}"`,
            icon: Plus,
            action: () => { onNewEvent(q); close(); },
          },
          {
            kind: 'command',
            id: 'create-task',
            label: `Create task "${q}"`,
            icon: CheckSquare,
            action: () => { onNewTask(q); close(); },
          },
        ],
      });
    }

    return result;
  }, [query, events, todos, calendars, spaces, onNavigateToDate, onNewEvent, onNewTask, onSwitchView, close]);

  // Flat item list for keyboard navigation
  const flatItems = useMemo(() => sections.flatMap(s => s.items), [sections]);

  // Keep selectedIndex in bounds
  useEffect(() => {
    setSelectedIndex(i => Math.min(i, Math.max(flatItems.length - 1, 0)));
  }, [flatItems.length]);

  // Auto-scroll selected item into view
  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // ── Keyboard handler ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => (i + 1) % Math.max(flatItems.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => (i - 1 + Math.max(flatItems.length, 1)) % Math.max(flatItems.length, 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        activateItem(flatItems[selectedIndex]);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, flatItems, selectedIndex, close]);

  const activateItem = useCallback((item: AnyItem | undefined) => {
    if (!item) return;
    if (item.kind === 'command') { item.action(); return; }
    if (item.kind === 'event') {
      if (item.event.dates.length) {
        const d = new Date(item.event.dates[0] + 'T00:00:00');
        // Stay in current view; fall back to month if currently in todo view
        onNavigateToDate(d, currentView === 'todo' ? 'month' : currentView);
      }
      onOpenEvent(item.event);
      close();
    } else if (item.kind === 'todo') {
      // Navigate to due date in current view (skip if in todo view or no due date)
      if (item.todo.due_date && currentView !== 'todo') {
        const d = new Date(item.todo.due_date + 'T00:00:00');
        onNavigateToDate(d, currentView);
      }
      onOpenTodo(item.todo);
      close();
    } else if (item.kind === 'calendar') {
      // Navigate and show calendar (no-op beyond closing for now)
      close();
    } else if (item.kind === 'space') {
      onSwitchSpace(item.space);
      close();
    }
  }, [onNavigateToDate, onOpenEvent, onOpenTodo, onSwitchSpace, close]);

  // ── Reset selection on query change ────────────────────────────────────────

  useEffect(() => { setSelectedIndex(0); }, [query]);

  if (!isOpen) return null;

  // ── Render ──────────────────────────────────────────────────────────────────

  let flatIdx = 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-start justify-center pt-[10vh] px-4"
      style={{ background: 'var(--modal-overlay)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="w-full max-w-[580px] bg-[var(--surface-elevated)] rounded-2xl shadow-2xl border border-[var(--border-primary)] overflow-hidden animate-fadeIn"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--border-secondary)]">
          <Search className="w-[15px] h-[15px] text-[var(--content-tertiary)] flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events, tasks, calendars…"
            className="flex-1 bg-transparent outline-none focus-visible:outline-none text-[15px] text-[var(--content-primary)] placeholder:text-[var(--content-tertiary)]"
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              onMouseDown={(e) => { e.preventDefault(); setQuery(''); inputRef.current?.focus(); }}
              className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[var(--content-tertiary)] hover:text-[var(--content-primary)] hover:bg-[var(--surface-tertiary)] transition-colors"
              aria-label="Clear"
            >
              <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 fill-current"><path d="M6 4.94L10.47.47a.75.75 0 111.06 1.06L7.06 6l4.47 4.47a.75.75 0 11-1.06 1.06L6 7.06l-4.47 4.47A.75.75 0 11.47 10.47L4.94 6 .47 1.53A.75.75 0 111.53.47L6 4.94z"/></svg>
            </button>
          )}
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="overflow-y-auto py-2"
          style={{ maxHeight: '440px' }}
        >
          {flatItems.length === 0 && (
            <p className="px-5 py-10 text-center text-[13px] text-[var(--content-tertiary)]">
              No results
            </p>
          )}

          {sections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-1 pt-1 border-t border-[var(--border-secondary)]' : ''}>
              {section.title && (
                <p className="px-5 pt-2 pb-1 text-[11px] font-medium text-[var(--content-tertiary)]">
                  {section.title}
                </p>
              )}
              <div className="px-2">
                {section.items.map((item) => {
                  const idx = flatIdx++;
                  const isSelected = idx === selectedIndex;
                  return (
                    <ResultRow
                      key={itemKey(item)}
                      item={item}
                      isSelected={isSelected}
                      isDark={isDark}
                      ref={(el) => { itemRefs.current[idx] = el; }}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      onClick={() => activateItem(item)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          <div className="h-2" />
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── ResultRow ─────────────────────────────────────────────────────────────────

interface ResultRowProps {
  item: AnyItem;
  isSelected: boolean;
  isDark: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

const ResultRow = React.forwardRef<HTMLButtonElement, ResultRowProps>(
  ({ item, isSelected, isDark, onMouseEnter, onClick }, ref) => {
    const base = `w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl transition-colors ${
      isSelected ? 'bg-[var(--surface-tertiary)]' : 'hover:bg-[var(--surface-tertiary)]'
    }`;

    if (item.kind === 'command') {
      const Icon = item.icon;
      return (
        <button ref={ref} className={base} onMouseEnter={onMouseEnter} onClick={onClick}>
          <span className="flex-shrink-0 w-8 h-8 rounded-xl border border-[var(--border-primary)] flex items-center justify-center">
            <Icon className="w-4 h-4 text-[var(--content-secondary)]" />
          </span>
          <span className="flex-1 text-[14px] text-[var(--content-primary)]">{item.label}</span>
          {item.hint && (
            <span className="text-[11px] text-[var(--content-tertiary)]">{item.hint}</span>
          )}
        </button>
      );
    }

    if (item.kind === 'event') {
      const color = item.event.color?.bg || 'bg-unassigned';
      const dateLabel = formatEventDate(item.event.dates);
      return (
        <button ref={ref} className={base} onMouseEnter={onMouseEnter} onClick={onClick}>
          <ColorDot color={color} isDark={isDark} />
          <span className="flex-1 min-w-0 text-[14px] text-[var(--content-primary)] truncate">
            {item.event.title || 'Untitled'}
          </span>
          {item.cal && (
            <span className="text-[11px] text-[var(--content-tertiary)] flex-shrink-0 hidden sm:block truncate max-w-[90px]">
              {item.cal.name}
            </span>
          )}
          {dateLabel && (
            <span className={`text-[11px] flex-shrink-0 font-medium ${
              dateLabel === 'Today' ? 'text-[var(--accent-primary)]' : 'text-[var(--content-tertiary)]'
            }`}>
              {dateLabel}
            </span>
          )}
        </button>
      );
    }

    if (item.kind === 'todo') {
      const { todo } = item;
      const isComplete = !!(todo.completed_at);
      const due = formatTodoDue(todo.due_date);
      return (
        <button ref={ref} className={base} onMouseEnter={onMouseEnter} onClick={onClick}>
          <span className={`flex-shrink-0 w-8 h-8 rounded-xl border flex items-center justify-center transition-colors ${
            isComplete
              ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)]'
              : 'border-[var(--border-primary)]'
          }`}>
            {isComplete
              ? <svg viewBox="0 0 10 8" className="w-3 h-3 text-white fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1,4 4,7 9,1" /></svg>
              : <CheckSquare className="w-4 h-4 text-[var(--content-tertiary)]" />
            }
          </span>
          <span className={`flex-1 min-w-0 text-[14px] truncate ${
            isComplete ? 'line-through text-[var(--content-tertiary)]' : 'text-[var(--content-primary)]'
          }`}>
            {todo.title || 'Untitled task'}
          </span>
          {due && (
            <span className={`text-[11px] flex-shrink-0 font-medium ${
              due.overdue ? 'text-[var(--error)]' : due.label === 'Today' ? 'text-[var(--accent-primary)]' : 'text-[var(--content-tertiary)]'
            }`}>
              {due.label}
            </span>
          )}
        </button>
      );
    }

    if (item.kind === 'calendar') {
      const { cal } = item;
      return (
        <button ref={ref} className={base} onMouseEnter={onMouseEnter} onClick={onClick}>
          <ColorDot color={cal.color} isDark={isDark} />
          <span className="flex-1 text-[14px] text-[var(--content-primary)]">{cal.name}</span>
          <span className="text-[11px] text-[var(--content-tertiary)]">Calendar</span>
        </button>
      );
    }

    if (item.kind === 'space') {
      const { space } = item;
      return (
        <button ref={ref} className={base} onMouseEnter={onMouseEnter} onClick={onClick}>
          <span className="flex-shrink-0 w-8 h-8 rounded-xl border border-[var(--border-primary)] flex items-center justify-center overflow-hidden">
            {space.icon_url
              ? <img src={space.icon_url} alt="" className="w-full h-full object-cover" />
              : <Users className="w-4 h-4 text-[var(--content-secondary)]" />
            }
          </span>
          <span className="flex-1 text-[14px] text-[var(--content-primary)]">{space.name}</span>
          <span className="text-[11px] text-[var(--content-tertiary)]">Switch space</span>
        </button>
      );
    }

    return null;
  }
);

ResultRow.displayName = 'ResultRow';

function itemKey(item: AnyItem): string {
  if (item.kind === 'command')  return `cmd-${item.id}`;
  if (item.kind === 'event')    return `event-${item.event.id}`;
  if (item.kind === 'todo')     return `todo-${item.todo.id}`;
  if (item.kind === 'calendar') return `cal-${item.cal.id}`;
  if (item.kind === 'space')    return `space-${item.space.id}`;
  return '';
}
